import { FunctionComponent } from "react";

import {
	usePreviewSalesforceFieldMappingsMutation,
	useTestSalesforceSyncMutation,
} from "@/pages/Private/redux/salesforce/salesforce.api";

import { SalesforceEntityType, SalesforceField, SalesforceSourceField } from "../schema/salesforce";
import {
	buildSalesforceMappingPreviewLabels,
	buildSalesforcePreviewOutcome,
	buildSalesforceTestSyncOutcome,
	MappingPreviewModal,
} from "./fieldMapping";
import { AiConfigProspectOption } from "./fieldMapping/aiConfig.types";

type Ts = (key: string, options?: Record<string, unknown>) => string;

interface SalesforceMappingPreviewModalProps {
	isOpen: boolean;
	entityType: SalesforceEntityType;
	sourceFields: SalesforceSourceField[];
	salesforceFields: SalesforceField[];
	ts: Ts;
	onClose: () => void;
	onSearchProspects: (query: string) => Promise<AiConfigProspectOption[]>;
	onCancelSearchProspects?: () => void;
}

/**
 * Salesforce binding for the generic MappingPreviewModal: the dry-run preview (whose values are
 * the **coerced** ones the sync would write, and whose per-row `skipReason` is the API's own
 * verdict) plus the real single-lead test sync.
 *
 * The test sync charges AI credits and, for OPPORTUNITY and LEAD, creates a **new** record on
 * every press — the per-entity warning copy says so before the button is pressed.
 */
export const SalesforceMappingPreviewModal: FunctionComponent<
	SalesforceMappingPreviewModalProps
> = ({
	isOpen,
	entityType,
	sourceFields,
	salesforceFields,
	ts,
	onClose,
	onSearchProspects,
	onCancelSearchProspects,
}) => {
	const [previewMappings] = usePreviewSalesforceFieldMappingsMutation();
	const [testSync] = useTestSalesforceSyncMutation();

	return (
		<MappingPreviewModal
			isOpen={isOpen}
			labels={buildSalesforceMappingPreviewLabels(ts, entityType)}
			onCancelSearchProspects={onCancelSearchProspects}
			onClose={onClose}
			onRunPreview={async (prospectId) =>
				buildSalesforcePreviewOutcome(
					await previewMappings({ prospectId, entityType }).unwrap(),
					sourceFields,
					salesforceFields,
					ts
				)
			}
			onSearchProspects={onSearchProspects}
			onTestSync={async (prospectId) =>
				buildSalesforceTestSyncOutcome(await testSync({ prospectId, entityType }).unwrap(), ts)
			}
		/>
	);
};
